/**
 * Schedule Lines Table with full feature parity to PO Lines.
 *
 * Supports sortable columns, filter panel, column show/hide with
 * localStorage persistence, CSV export, status badges, tab counts,
 * select-all / bulk actions, and per-row collaboration actions.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import MySuppliersToggle from '../../MySuppliersToggle';
import { purchasingAPI } from '../../../../lib/api';
import type { POLineSchedule } from '../../../../lib/api/types/purchasing';
import { getApiErrorMessage } from '../../../../utils/errorUtils';
import { useView } from '../../../../contexts/ViewContext';
import { useFormatters } from '../../../../contexts/PreferencesContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { viewerSideOf } from '../../lineStatus';

import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../../ui/tabs';
import { Pagination } from '../../../ui/pagination';
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Loader2,
  Search,
  CalendarRange,
  Columns,
  Download,
  RefreshCw,
  Eye,
  EyeOff,
  GripVertical,
  Check,
  X,
  Pause,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  ScheduleColumnConfig,
  DEFAULT_SCHEDULE_COLUMNS,
  SCHEDULE_COLUMN_STORAGE_KEY,
  ScheduleFilters,
  DEFAULT_SCHEDULE_FILTERS,
  SCHEDULE_TENANT_DEFAULT_FILTERS,
  ScheduleTab,
} from './types';
import { ScheduleFiltersPanel } from './ScheduleFiltersPanel';
import { ActiveFilterChips } from './ActiveFilterChips';
import { ScheduleLineRow } from './ScheduleLineRow';
import {
  AcceptScheduleModal,
  DeclineScheduleModal,
  ProposeScheduleDateModal,
  ProposeScheduleQtyModal,
  HoldScheduleModal,
  ReleaseScheduleModal,
  BulkAcceptModal,
  BulkRejectModal,
  BulkHoldModal,
} from './ScheduleModals';
import type { ModalType, ScheduleActionPayload } from './ScheduleModals';
import { useScheduleBulkActions } from './useScheduleBulkActions';
import ScheduleDetailModal from '../../ScheduleDetailModal';
import CloseHeldScheduleDialog from '../../CloseHeldScheduleDialog';
import { pageCountFromResponse, type PageSizeMemory } from '../../../../hooks/usePagination';

const PAGE_SIZE_CHOICES = [25, 100, 250, 500];

// Every acknowledge_status choice from PurchaseOrderLine.ACKNOWLEDGE_STATUS_CHOICES
// except 'closed'. Used by the Due This/Next Week tabs to show the full
// week's plan without the terminal CR-3410 collaboration closures.
const ACK_STATUSES_NOT_CLOSED = [
  'new',
  'declined',
  'change_proposed_supplier',
  'wip',
  'accepted',
  'change_proposed_customer',
  'despatched',
  'change_declined',
  'cancelled',
  'queried',
].join(',');

// Schedule statuses that still represent live work. 'shipped' and 'cancelled'
// are excluded: those are what the row renders as the grey "Closed" badge
// (see isClosed in ScheduleLineRow) — the line is done or dropped, so it is
// not part of any "due" or forecast plan and only pads the counts.
const OPEN_SCHEDULE_STATUSES = 'scheduled,active,partially_shipped,missed';
// The Open tab's variant. Arrears live on 'Due From Previous Weeks', which is
// the tab that exists to hold them and is where anyone chasing them looks;
// carrying them in Open too listed the same line twice under two headings and
// padded the Open count with work that is late rather than outstanding
// (customer review 2026-09-08, reversing the earlier "missed is overdue +
// outstanding, so it counts as open arrears" reading).
const OPEN_TAB_STATUSES = 'scheduled,active,partially_shipped';

// ----- Column persistence -----

// Column config (visibility, order, AND resized width) is persisted per user
// so two people sharing a browser don't clobber each other's layout.
function columnStorageKey(userId?: number | string): string {
  return userId != null
    ? `${SCHEDULE_COLUMN_STORAGE_KEY}:${userId}`
    : SCHEDULE_COLUMN_STORAGE_KEY;
}

function loadColumns(userId?: number | string): ScheduleColumnConfig[] {
  try {
    const stored = localStorage.getItem(columnStorageKey(userId));
    if (!stored) return DEFAULT_SCHEDULE_COLUMNS;
    const parsed = JSON.parse(stored) as ScheduleColumnConfig[];
    const storedMap = new Map(parsed.map((c) => [c.id, c]));
    return DEFAULT_SCHEDULE_COLUMNS.map((def) => {
      const saved = storedMap.get(def.id);
      return saved
        ? { ...def, visible: saved.visible, order: saved.order, width: saved.width }
        : def;
    }).sort((a, b) => a.order - b.order);
  } catch {
    return DEFAULT_SCHEDULE_COLUMNS;
  }
}

function saveColumns(cols: ScheduleColumnConfig[], userId?: number | string) {
  localStorage.setItem(columnStorageKey(userId), JSON.stringify(cols));
}

const MIN_COLUMN_WIDTH = 60;
const CHECKBOX_COL_WIDTH = 50;
// Header cell chrome that sits alongside the label text: px-4 padding (16px
// each side) + sort icon & gap for sortable columns.
const HEADER_PADDING_PX = 32;
const SORT_ICON_PX = 20;
// Generous buffer so canvas text measurement (which can under-estimate the
// real rendered webfont by several px) never leaves a label clipped by default.
const LABEL_BUFFER_PX = 16;

/** Measure rendered width of a header label in the actual header font, so the
 *  default column width can be sized to never clip the label. Cached canvas;
 *  falls back to a char estimate when 2D canvas is unavailable (e.g. jsdom). */
let _measureCtx: CanvasRenderingContext2D | null | undefined;
function measureLabelPx(label: string): number {
  try {
    if (_measureCtx === undefined) {
      _measureCtx = document.createElement('canvas').getContext('2d');
      if (_measureCtx) _measureCtx.font = '500 14px ui-sans-serif, system-ui, -apple-system, sans-serif';
    }
    if (_measureCtx) return _measureCtx.measureText(label).width;
  } catch {
    /* fall through to estimate */
  }
  return label.length * 7.5;
}

/** Smallest width that still shows the whole header label (text + padding +
 *  sort icon). Used as the default-column floor so labels are never hidden. */
function labelFitWidthPx(col: ScheduleColumnConfig): number {
  return Math.ceil(measureLabelPx(col.label))
    + HEADER_PADDING_PX
    + (col.sortable ? SORT_ICON_PX : 0)
    + LABEL_BUFFER_PX;
}

/** Effective pixel width for a column. A user-resized width is honoured as-is
 *  (they may deliberately shrink it). Otherwise the default is the larger of
 *  the configured ``minWidth`` and the width needed to show the full label, so
 *  by default no header label is truncated. */
function colWidthPx(col: ScheduleColumnConfig): number {
  if (typeof col.width === 'number' && col.width > 0) return col.width;
  const parsed = parseInt(col.minWidth, 10);
  const base = Number.isFinite(parsed) ? parsed : 120;
  return Math.max(base, labelFitWidthPx(col));
}

function getSortIcon(field: string, sortField: string | null, sortDir: 'asc' | 'desc') {
  if (sortField !== field) return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
  return sortDir === 'asc'
    ? <ArrowUp className="h-3 w-3 text-primary" />
    : <ArrowDown className="h-3 w-3 text-primary" />;
}

// ----- CSV export is server-side (see handleExport in component) -----

export default function ScheduleLinesTable() {
  const { getViewQueryParams, isLoading: viewContextLoading } = useView();
  const { formatDate, formatNumber } = useFormatters();
  const { user } = useAuth();

  const isSupplier = user?.platform === 'SUPPLIER';

  const [schedules, setSchedules] = useState<POLineSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const servedPageRef = useRef<PageSizeMemory | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(() => {
    try {
      const stored = Number(localStorage.getItem('scheduleLinesPageSize'));
      return PAGE_SIZE_CHOICES.includes(stored) ? stored : 25;
    } catch {
      return 25;
    }
  });
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    try {
      localStorage.setItem('scheduleLinesPageSize', String(size));
    } catch {
      // storage unavailable — the session still works, just unremembered
    }
  }, []);

  const [needsActionCount, setNeedsActionCount] = useState<number | null>(null);
  const [duePreviousWeeksCount, setDuePreviousWeeksCount] = useState<number | null>(null);
  const [dueThisWeekCount, setDueThisWeekCount] = useState<number | null>(null);
  const [dueNextWeekCount, setDueNextWeekCount] = useState<number | null>(null);
  const [openCount, setOpenCount] = useState<number | null>(null);
  const [onHoldCount, setOnHoldCount] = useState<number | null>(null);
  const [forecastedCount, setForecastedCount] = useState<number | null>(null);
  // True while the batched tab-counts request is in flight — the numbers
  // stay visible but pulse so stale values are recognisable as refreshing.
  const [countsRefreshing, setCountsRefreshing] = useState(false);

  const getThisWeekRange = useCallback(() => {
    const today = new Date();
    const daysSinceMonday = (today.getDay() + 6) % 7;
    const monday = new Date(today);
    monday.setDate(today.getDate() - daysSinceMonday);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { from: fmt(monday), to: fmt(sunday) };
  }, []);

  const getNextWeekRange = useCallback(() => {
    const today = new Date();
    const daysSinceMonday = (today.getDay() + 6) % 7;
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() - daysSinceMonday + 7);
    nextMonday.setHours(0, 0, 0, 0);
    const nextSunday = new Date(nextMonday);
    nextSunday.setDate(nextMonday.getDate() + 6);
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { from: fmt(nextMonday), to: fmt(nextSunday) };
  }, []);

  // Buyer-code scoping: users with allocated ERP buyer codes default to
  // "my suppliers"; the toggle lifts the scope via all_suppliers=true.
  const buyerCodes = user?.buyer_codes ?? [];
  const [mySuppliersOnly, setMySuppliersOnly] = useState(true);

  // Daily-digest deep link: ?sched_tab=<quick filter> pre-selects the tab
  // so an emailed count lands on the same population it described.
  const [urlParams] = useSearchParams();
  const VALID_SCHED_TABS: ReadonlyArray<ScheduleTab> = [
    'all', 'needs_action', 'due_previous_weeks', 'due_this_week',
    'due_next_week', 'open', 'closed', 'on_hold', 'forecasted',
  ];
  const urlSchedTab = urlParams.get('sched_tab') as ScheduleTab | null;
  const [activeTab, setActiveTab] = useState<ScheduleTab>(
    urlSchedTab && VALID_SCHED_TABS.includes(urlSchedTab)
      ? urlSchedTab
      : (isSupplier ? 'open' : 'due_this_week'),
  );
  const [sortField, setSortField] = useState<string | null>('schedule_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showFilters, setShowFilters] = useState(false);
  // Schedule-line tenants open on scheduling agreements only, with
  // OTIF-excluded orders hidden — the population the Scheduled Dashboard
  // scores. Shown as chips (never silent); each × clears one, Clear all
  // clears the lot. ``import_handler`` comes with the auth user, so there is
  // no flags-loading race to guard against. CUSTOMER side only: a supplier
  // owes every line it has been sent, so it opens on all of them.
  const applyTenantDefaults = user?.import_handler === 'dennis_eagle' && !isSupplier;
  const [filters, setFilters] = useState<ScheduleFilters>(() => (
    applyTenantDefaults
      ? { ...DEFAULT_SCHEDULE_FILTERS, ...SCHEDULE_TENANT_DEFAULT_FILTERS }
      : DEFAULT_SCHEDULE_FILTERS
  ));
  // Which chips read "default": the tenant defaults, while they still hold
  // the default value. A user picking the same value by hand is the same
  // filter, so it keeps the tag — the tag says why the list is narrowed.
  const defaultFilterKeys = useMemo(() => new Set(
    (Object.keys(SCHEDULE_TENANT_DEFAULT_FILTERS) as Array<keyof ScheduleFilters>)
      .filter((k) => applyTenantDefaults && filters[k] === SCHEDULE_TENANT_DEFAULT_FILTERS[k]),
  ), [filters, applyTenantDefaults]);

  const userId = user?.id;
  // ERP Balance Qty is the ERP's own open qty and Buyer Code the customer's
  // own material-controller code — customer side, and only for the tenant
  // whose import captures them. Nobody else is offered the columns (the API
  // drops both for supplier viewers).
  const showErpBalance = !isSupplier && user?.import_handler === 'dennis_eagle';
  const withAvailableColumns = (cols: ScheduleColumnConfig[]) =>
    showErpBalance ? cols : cols.filter((c) => c.id !== 'erp_balance_qty' && c.id !== 'buyer_code');
  const [columns, setColumns] = useState<ScheduleColumnConfig[]>(() => withAvailableColumns(loadColumns(userId)));
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef<HTMLDivElement>(null);
  // Active column-resize drag (null when not resizing). Tracked in a ref so the
  // pointermove/up listeners always see the latest start values.
  const resizeRef = useRef<{ id: string; startX: number; startWidth: number } | null>(null);

  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);

  const [modal, setModal] = useState<ModalType>(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Close column dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(e.target as Node)) {
        setShowColumnDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ----- Data fetching -----

  // Tab-state + filter-panel params (no pagination / sort). Shared by the list
  // query AND the quick-filter counts so the counts always reflect the SAME
  // filtered dataset the list shows — including the state filters (schedule
  // status / on-hold / ack / date range), not just the identity filters.
  const tabAndFilterParams = useCallback((tab: ScheduleTab) => {
    const params: Record<string, string | number | boolean> = {};
    for (const [k, v] of Object.entries(getViewQueryParams())) {
      if (v !== undefined) params[k] = v;
    }
    // Buyer-code scope — sent explicitly (true AND false) so the CSV export
    // honours the toggle too: the list scopes to "my suppliers" by default and
    // all_suppliers=true lifts it, while on an export the backend only scopes
    // when it sees all_suppliers=false. Shared by the list fetch and export.
    if (buyerCodes.length > 0) params['all_suppliers'] = !mySuppliersOnly;
    if (tab === 'needs_action') params['needs_action'] = true;
    // Due From Previous Weeks: still-open arrears with schedule_date before
    // this week's Monday. Kept as its own tab so Due This Week stays clean.
    if (tab === 'due_previous_weeks') {
      const { from } = getThisWeekRange();
      const lastSunday = new Date(from);
      lastSunday.setDate(lastSunday.getDate() - 1);
      const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      params['schedule_date__lte'] = fmt(lastSunday);
      // Include 'missed' — the DE active-schedule arrears status. Past-due
      // outstanding schedules are relabelled scheduled/active -> missed, so
      // omitting it emptied this tab. PNA never produces 'missed' (no-op there).
      params['status__in'] = OPEN_SCHEDULE_STATUSES;
      params['outstanding'] = true;
      params['on_hold'] = false;
      params['acknowledge_status__in'] = ACK_STATUSES_NOT_CLOSED;
    }
    // Due This Week = Mon-Sun of THIS week only. Older arrears live in
    // 'Due From Previous Weeks' so this tab is a clean planner view.
    if (tab === 'due_this_week') {
      const { from, to } = getThisWeekRange();
      params['schedule_date__gte'] = from;
      params['schedule_date__lte'] = to;
      // Include 'missed' — as the week progresses, schedules dated earlier in
      // the week tip past-due (status 'missed') but are still due THIS week.
      // Excluding them made the count shrink daily (e.g. 1007 -> 15 mid-week).
      // Matches the Previous/Next-week tabs which already include 'missed'.
      params['status__in'] = OPEN_SCHEDULE_STATUSES;
      params['outstanding'] = true;
      params['on_hold'] = false;
      params['acknowledge_status__in'] = ACK_STATUSES_NOT_CLOSED;
    }
    // Due Next Week = full Mon-Sun of next week. Previously this carried no
    // status filter, so shipped/cancelled ("Closed") rows appeared here — the
    // earlier reading was that a shipped row in a future window means
    // "received ahead of schedule" and belongs in the plan. Customer review
    // 2026-07-16: a closed line is finished work and should not show in a
    // forward plan or inflate its count, same as the other Due tabs.
    if (tab === 'due_next_week') {
      const { from, to } = getNextWeekRange();
      params['schedule_date__gte'] = from;
      params['schedule_date__lte'] = to;
      params['status__in'] = OPEN_SCHEDULE_STATUSES;
      params['on_hold'] = false;
      params['acknowledge_status__in'] = ACK_STATUSES_NOT_CLOSED;
    }
    if (tab === 'open') {
      // Arrears ('missed') are excluded here — see OPEN_TAB_STATUSES. They
      // remain on 'Due From Previous Weeks'.
      params['status__in'] = OPEN_TAB_STATUSES;
      params['is_forecast'] = false;
      params['outstanding'] = true;
    }
    if (tab === 'closed') params['status'] = 'shipped';
    if (tab === 'on_hold') {
      params['on_hold'] = true;
      // CR-7322: the daily digest's split hold rows deep-link to the On Hold
      // tab narrowed to who placed the hold, so the grid count matches the
      // emailed count. 'customer' covers helix / NULL (see purchasing.hold_scope).
      const placedBy = urlParams.get('on_hold_placed_by');
      if (placedBy === 'customer' || placedBy === 'supplier') params['on_hold_placed_by'] = placedBy;
    }
    if (tab === 'forecasted') {
      params['is_forecast'] = true;
      // Closed lines are not forecast demand — without this, cancelled rows
      // padded the tab and its count (customer review 2026-07-16).
      params['status__in'] = OPEN_SCHEDULE_STATUSES;
    }
    if (filters.poNumber) params['search'] = filters.poNumber;
    if (filters.lineNumber) params['po_line__line_number'] = filters.lineNumber;
    if (filters.scheduleLineNumber) params['schedule_line_number'] = filters.scheduleLineNumber;
    if (filters.partNumber) params['po_line__part_number__icontains'] = filters.partNumber;
    if (filters.supplierPartNumber) params['supplier_part_number'] = filters.supplierPartNumber;
    // Quick box: one term, matched against part number OR supplier part no.
    if (filters.partSearch) params['part_search'] = filters.partSearch;
    if (filters.supplier) params['po_line__po__supplier__name__icontains'] = filters.supplier;
    if (filters.warehouse) params['warehouse'] = filters.warehouse;
    if (filters.erpOrderType) params['po_line__po__erp_order_type'] = filters.erpOrderType;
    // OTIF exclusion: 'hide' drops orders/lines flagged exclude_from_otif,
    // 'only' isolates them. Server param is a plain true/false.
    if (filters.otifExcluded === 'hide') params['otif_excluded'] = false;
    if (filters.otifExcluded === 'only') params['otif_excluded'] = true;
    if (filters.acknowledgeStatus === 'forecast') {
      // Pseudo-status: filter to only the firm-boundary-derived forecast rows.
      params['is_forecast'] = true;
    } else if (filters.acknowledgeStatus === 'missed') {
      // Pseudo-status, same shape as 'forecast': the Ack Status column renders
      // a "Missed" pill for these rows, so the filter beside it has to offer
      // Missed or the column shows a value you cannot filter on. It is backed
      // by schedule status, because acknowledge_status is never 'missed' —
      // the ERP never acknowledges, so those rows sit at 'new'.
      params['status'] = 'missed';
    } else if (filters.acknowledgeStatus) {
      params['acknowledge_status'] = filters.acknowledgeStatus;
    }
    // Schedule Status wins if both are set — it is the explicit control for
    // this field, and letting the Ack pseudo-status overwrite it would make
    // the two dropdowns silently contradict each other.
    if (filters.scheduleStatus) params['status'] = filters.scheduleStatus;
    if (filters.onHold === 'yes') params['on_hold'] = true;
    if (filters.onHold === 'no') params['on_hold'] = false;
    if (filters.scheduleDateFrom) params['schedule_date__gte'] = filters.scheduleDateFrom;
    if (filters.scheduleDateTo) params['schedule_date__lte'] = filters.scheduleDateTo;
    if (filters.promiseDateFrom) params['supplier_promise_date__gte'] = filters.promiseDateFrom;
    if (filters.promiseDateTo) params['supplier_promise_date__lte'] = filters.promiseDateTo;
    return params;
    // CR-7322: urlParams is read for on_hold_placed_by, so it must be a dep —
    // otherwise a same-route deep-link change would keep the stale value.
  }, [getViewQueryParams, filters, getThisWeekRange, getNextWeekRange, buyerCodes.length, mySuppliersOnly, urlParams]);

  const buildParams = useCallback((tab: ScheduleTab, page: number) => {
    const params: Record<string, string | number | boolean> = {
      ...tabAndFilterParams(tab), page, page_size: pageSize,
    };
    if (sortField) {
      // Map frontend column IDs to backend ordering fields
      const SORT_MAP: Record<string, string> = {
        schedule_line_number: 'schedule_line_number_int',
        line_number: 'po_line__line_number',
        po_number: 'po_line__po__po_number',
        part_number: 'po_line__part_number',
        supplier_name: 'po_line__po__supplier__name',
        buyer_code: 'po_line__buyer_id',
      };
      const backendField = SORT_MAP[sortField] || sortField;
      params['ordering'] = sortDirection === 'desc' ? `-${backendField}` : backendField;
    }
    return params;
  }, [tabAndFilterParams, sortField, sortDirection, pageSize]);

  const handleExport = useCallback(async () => {
    // Server-side export — streams every row matching the current tab +
    // panel filters, bypassing UI pagination so users don't end up
    // with just the 25 rows currently on screen. Both endpoints already
    // exist server-side; the button offers CSV and Excel.
    try {
      const exportParams = { ...buildParams(activeTab, 1) };
      delete exportParams['page'];
      delete exportParams['page_size'];
      const qs = new URLSearchParams(
        Object.entries(exportParams)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ).toString();
      const apiClientModule = await import('../../../../lib/api/client');
      const endpoint = 'export-xlsx';  // CSV retired — Excel only
      const resp = await apiClientModule.default.get(
        `/purchasing/schedules/${endpoint}/?${qs}`,
        { responseType: 'blob' },
      );
      const mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([resp.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `schedule_lines_${activeTab}_${today}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel exported successfully');
    } catch (err) {
      console.error('Schedule export failed:', err);
      toast.error('Failed to export Excel');
    }
  }, [buildParams, activeTab]);

  // Post-action refetches must bypass the 60s Cache-Control browser cache on
  // the schedules list, or a just-proposed change replays with its stale
  // acknowledge status (customer review 2026-07-08). A ref (not state) so
  // setting it doesn't re-trigger the fetch effects on its own.
  const cacheBustRef = useRef<number | null>(null);
  // Monotonic ids of the newest list / tab-counts requests. Progressive
  // typing fires a request per debounce tick ("STA0" then "STA02"), and the
  // broader earlier query can return LAST because it matches far more rows —
  // without the guard its stale rows overwrite the correct narrower result
  // (user-reported: searching one PO briefly showed a different PO's rows).
  const listRequestRef = useRef(0);
  const tabCountsRequestRef = useRef(0);

  const fetchSchedules = useCallback(async () => {
    const requestId = ++listRequestRef.current;
    setLoading(true);
    try {
      const res = await purchasingAPI.getSchedules({
        ...buildParams(activeTab, currentPage),
        ...(cacheBustRef.current ? { _t: cacheBustRef.current } : {}),
      });
      if (requestId !== listRequestRef.current) return; // superseded
      setSchedules(res.data.results);
      setTotalCount(res.data.count);
      // CR-7279: page count from what the server served, not what was asked for.
      const paged = pageCountFromResponse(res.data.count, pageSize, res.data.results.length, currentPage, servedPageRef.current);
      servedPageRef.current = paged.memory;
      setTotalPages(paged.totalPages);
    } catch (err: unknown) {
      if (requestId !== listRequestRef.current) return;
      console.error('Failed to fetch schedules:', err);
      setSchedules([]);
    } finally {
      if (requestId === listRequestRef.current) setLoading(false);
    }
  }, [activeTab, currentPage, buildParams, pageSize]);

  const fetchTabCounts = useCallback(async () => {
    // Race guard: a superseded request's response must never render. On a
    // slow connection the page-load counts (unfiltered) can land AFTER the
    // user has already typed a quick search — flashing the unfiltered
    // numbers over the cleared badges for a beat before the filtered
    // response corrects them (user-reported with a PO-number search).
    const requestId = ++tabCountsRequestRef.current;
    setCountsRefreshing(true);
    // Clear the stale numbers immediately: until the fresh counts land the
    // tabs show no number at all. Leaving the old counts up while the table
    // already shows the new filtered rows read as a bug ("3 rows but the
    // tab says 265") — a blank count reads as loading.
    setNeedsActionCount(null);
    setDuePreviousWeeksCount(null);
    setDueThisWeekCount(null);
    setDueNextWeekCount(null);
    setOpenCount(null);
    setOnHoldCount(null);
    setForecastedCount(null);
    try {
      // CR-6543 r6: ONE batched request replaces seven parallel
      // page_size=1 probes — each of those paid the full request stack
      // (auth, filterset, a serialized row + overlay maps) and they
      // contended for the web dyno's threads, so the numbers rendered
      // seconds late and lagged filter changes. The endpoint runs each
      // param-set through the list view's own queryset pipeline, so
      // counts still always match what the list would show.
      const tabs: Record<string, Record<string, string | number | boolean>> = {};
      (['needs_action', 'due_previous_weeks', 'due_this_week', 'due_next_week',
        'open', 'on_hold', 'forecasted'] as ScheduleTab[]).forEach((tab) => {
        tabs[tab] = tabAndFilterParams(tab);
      });
      const res = await purchasingAPI.getScheduleTabCounts(tabs);
      if (requestId !== tabCountsRequestRef.current) return; // superseded
      const c = res.data.counts;
      setNeedsActionCount(c['needs_action'] ?? null);
      setDuePreviousWeeksCount(c['due_previous_weeks'] ?? null);
      setDueThisWeekCount(c['due_this_week'] ?? null);
      setDueNextWeekCount(c['due_next_week'] ?? null);
      setOpenCount(c['open'] ?? null);
      setOnHoldCount(c['on_hold'] ?? null);
      setForecastedCount(c['forecasted'] ?? null);
    } catch (err: unknown) { console.error('Failed to fetch tab counts:', err); }
    finally {
      if (requestId === tabCountsRequestRef.current) setCountsRefreshing(false);
    }
  }, [tabAndFilterParams]);

  // List: refetch on tab / page / filter / view change.
  useEffect(() => {
    if (!viewContextLoading) fetchSchedules();
  }, [viewContextLoading, fetchSchedules]);

  // Tab counts: recompute ONLY when filters or view change — NOT on tab/page
  // switch. Fires in the SAME tick as the list fetch: every input that can
  // change the params is already debounced upstream (quick search 500ms,
  // panel filters auto-apply after typing settles), so an extra delay here
  // only made the counts start after the results had already rendered —
  // the tabs sat on stale numbers while the table showed the new rows.
  useEffect(() => {
    if (viewContextLoading) return;
    fetchTabCounts();
  }, [viewContextLoading, fetchTabCounts]);

  // Explicit refreshes are always post-mutation (row/bulk workflow actions,
  // the Refresh button) — stamp a fresh cache-buster so the reload reflects
  // the change immediately instead of the 60s-cached response.
  const refresh = () => {
    cacheBustRef.current = Date.now();
    fetchSchedules();
    fetchTabCounts();
  };

  // ----- Bulk actions -----

  const bulk = useScheduleBulkActions({ schedules, refresh });

  // ----- Handlers -----

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handleFilterChange = (key: keyof ScheduleFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };
  const handleSearch = () => setCurrentPage(1);

  /**
   * Always-visible quick Part Number search (matches the Deliveries tab).
   *
   * Drives the same ``filters.partNumber`` the Show Filters panel uses — one
   * source of truth, so the two inputs can never disagree — and debounces the
   * refetch by the same 500ms rather than firing per keystroke.
   */
  const quickSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleQuickPartSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, partSearch: value }));
    if (quickSearchTimerRef.current) clearTimeout(quickSearchTimerRef.current);
    quickSearchTimerRef.current = setTimeout(() => setCurrentPage(1), 500);
  };
  useEffect(() => () => {
    if (quickSearchTimerRef.current) clearTimeout(quickSearchTimerRef.current);
  }, []);
  const handleClearFilters = () => { setFilters(DEFAULT_SCHEDULE_FILTERS); setCurrentPage(1); };
  const handleTabChange = (v: string) => { setActiveTab(v as ScheduleTab); setCurrentPage(1); bulk.setSelectedIds(new Set()); };

  // Column management
  const toggleColumn = (id: string) => {
    setColumns((prev) => { const n = prev.map((c) => c.id === id ? { ...c, visible: !c.visible } : c); saveColumns(n, userId); return n; });
  };
  const resetColumns = () => { const d = withAvailableColumns(DEFAULT_SCHEDULE_COLUMNS); setColumns(d); saveColumns(d, userId); };
  const selectAllCols = () => { setColumns((p) => { const n = p.map((c) => ({ ...c, visible: true })); saveColumns(n, userId); return n; }); };
  const reorderColumns = (from: number, to: number) => {
    setColumns((prev) => { const n = [...prev]; const [m] = n.splice(from, 1); n.splice(to, 0, m); const o = n.map((c, i) => ({ ...c, order: i })); saveColumns(o, userId); return o; });
  };

  // ----- Column resize (drag the right edge of a header cell) -----
  const startResize = (id: string, e: React.PointerEvent) => {
    // Don't let the drag also trigger the header's sort onClick.
    e.preventDefault();
    e.stopPropagation();
    const col = columns.find((c) => c.id === id);
    resizeRef.current = { id, startX: e.clientX, startWidth: col ? colWidthPx(col) : 120 };

    const onMove = (ev: PointerEvent) => {
      const r = resizeRef.current;
      if (!r) return;
      const next = Math.max(MIN_COLUMN_WIDTH, r.startWidth + (ev.clientX - r.startX));
      setColumns((prev) => prev.map((c) => (c.id === r.id ? { ...c, width: next } : c)));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.userSelect = '';
      resizeRef.current = null;
      // Persist the final widths for this user.
      setColumns((prev) => { saveColumns(prev, userId); return prev; });
    };
    // Disable text selection while dragging.
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Modal actions
  const openModal = (kind: string, schedule: POLineSchedule) => {
    setModal({ kind, schedule } as ModalType);
  };

  const runAction = async (fn: () => Promise<unknown>, successMsg: string, errorMsg: string) => {
    setModalLoading(true);
    try {
      await fn();
      toast.success(successMsg);
      setModal(null);
      refresh();
    } catch (err: unknown) {
      console.error(errorMsg, err);
      // Surface the API's own rejection reason — a bare generic message
      // reads as "the button does nothing" when the backend explains why.
      toast.error(getApiErrorMessage(err, errorMsg), { duration: 8000 });
      throw err;
    } finally {
      setModalLoading(false);
    }
  };

  const handleAcceptSubmit = async (data: ScheduleActionPayload) => {
    await runAction(
      () => purchasingAPI.acceptScheduleDate(data.scheduleId, { comments: data.comments }),
      'Delivery date accepted',
      'Failed to accept delivery date',
    );
  };

  const handleDeclineSubmit = async (data: ScheduleActionPayload) => {
    await runAction(
      () => purchasingAPI.declineScheduleDate(data.scheduleId, { reason: data.reason || '', comments: data.comments }),
      'Delivery date declined',
      'Failed to decline delivery date',
    );
  };

  const handleProposeDateSubmit = async (data: ScheduleActionPayload) => {
    await runAction(
      () => purchasingAPI.proposeScheduleDate(data.scheduleId, { proposed_date: data.proposedDate || '', reason: data.reason || '', comments: data.comments }),
      'Alternative date proposed',
      'Failed to propose alternative date',
    );
  };

  const handleProposeQtySubmit = async (data: ScheduleActionPayload) => {
    await runAction(
      () => purchasingAPI.proposeScheduleQty(data.scheduleId, { proposed_quantity: data.proposedQty || 0, reason: data.reason || '', comments: data.comments }),
      'Quantity change proposed',
      'Failed to propose quantity change',
    );
  };

  const handleHoldSubmit = async (data: ScheduleActionPayload) => {
    await runAction(
      () => purchasingAPI.putScheduleOnHold(data.scheduleId, { reason: data.reason || '' }),
      'Schedule placed on hold',
      'Failed to put schedule on hold',
    );
  };

  const handleReleaseSubmit = async (data: ScheduleActionPayload) => {
    await runAction(
      () => purchasingAPI.releaseScheduleHold(data.scheduleId),
      'Hold released',
      'Failed to release hold',
    );
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== '');
  // The Exceptions tab shows the reason column whether or not it is switched
  // on in Columns: a tab whose whole premise is "these need attention" that
  // does not say why makes you open every row to find out. It stays a normal
  // toggle everywhere else, and switching it on there persists as usual —
  // this only forces it in the one place it is the point of the view.
  const visibleCols = columns.filter(
    (c) => c.visible || (activeTab === 'needs_action' && c.id === 'exception_reason'),
  );
  // Fixed-layout table width = checkbox column + each visible column's
  // (possibly user-resized) width. Drives the colgroup so resized widths are
  // authoritative for both header and body cells.
  const totalTableWidth = CHECKBOX_COL_WIDTH + visibleCols.reduce((sum, c) => sum + colWidthPx(c), 0);

  return (
    <div className="space-y-4">
      {showFilters && (
        <ScheduleFiltersPanel filters={filters} onFilterChange={handleFilterChange} onSearch={handleSearch} onClearFilters={handleClearFilters} />
      )}

      <Card>
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CalendarRange className="h-5 w-5" />PO Line Schedules
                  {!loading && <Badge variant="secondary" className="ml-2 text-xs">{totalCount} schedules</Badge>}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">Showing {schedules.length} of {totalCount.toLocaleString()} schedule lines</p>
              </div>
              <div className="flex items-center gap-3">
                <MySuppliersToggle myOnly={mySuppliersOnly} onChange={(v) => { setMySuppliersOnly(v); setCurrentPage(1); }} codes={buyerCodes} />
                {/* Always-visible quick search — one debounced term matched
                    against part number, supplier part reference, PO number or
                    supplier name (API param part_search). CR-7232: supplier
                    was added here as well as PO Lines, so the two toolbars
                    keep answering the same term the same way. */}
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-foreground pointer-events-none" />
                  <input
                    type="text"
                    value={filters.partSearch}
                    onChange={(e) => handleQuickPartSearch(e.target.value)}
                    placeholder="Search Part, Supplier Part, PO or Supplier"
                    // border-foreground rather than a literal black: it reads
                    // as black in light mode and stays visible in dark mode,
                    // where a hardcoded black border would disappear.
                    // w-80: the placeholder names four fields since CR-7232, so
                    // it needs the room — at w-56 it was clipped mid-word.
                    className="pl-8 pr-2 py-1.5 text-sm border-2 border-foreground rounded-lg w-80 bg-background"
                    aria-label="Quick search by part number, supplier part number, PO number or supplier name"
                  />
                </div>
                <button onClick={() => setShowFilters(!showFilters)} className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 ${showFilters ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}><Search className="h-3 w-3" />Filters</button>
                {/* Excel-only (customer decision 2026-09-04): CSV was
                    retired, so the button exports .xlsx directly. */}
                <button onClick={() => handleExport()} disabled={loading} className="px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed" title="Export all rows matching current tab + filters to Excel (streamed server-side, not just the current page)"><Download className="h-3 w-3" />Export</button>
                <Button variant="outline" size="sm" onClick={refresh} disabled={loading}><RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>

                {/* Column Visibility Dropdown */}
                <div className="relative" ref={columnDropdownRef}>
                  <button onClick={() => setShowColumnDropdown(!showColumnDropdown)} className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 ${showColumnDropdown ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}><Columns className="h-3 w-3" />Columns</button>
                  {showColumnDropdown && (
                    <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-border rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto">
                      <div className="p-2 border-b flex items-center justify-between">
                        <span className="text-sm font-medium">Manage Columns</span>
                        <div className="flex items-center gap-2">
                          <button onClick={selectAllCols} className="text-xs text-primary hover:underline">Select All</button>
                          <span className="text-xs text-muted-foreground">|</span>
                          <button onClick={resetColumns} className="text-xs text-primary hover:underline">Reset</button>
                        </div>
                      </div>
                      <div className="px-2 py-1 text-xs text-muted-foreground border-b bg-muted/30">Drag to reorder - Click to show/hide</div>
                      <div className="p-2 space-y-1">
                        {columns.map((col, idx) => (
                          <div key={col.id} draggable
                            onDragStart={(e) => { e.dataTransfer.setData('text/plain', idx.toString()); e.currentTarget.classList.add('opacity-50'); }}
                            onDragEnd={(e) => e.currentTarget.classList.remove('opacity-50')}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-primary/10'); }}
                            onDragLeave={(e) => e.currentTarget.classList.remove('bg-primary/10')}
                            onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('bg-primary/10'); const di = parseInt(e.dataTransfer.getData('text/plain'), 10); if (di !== idx) reorderColumns(di, idx); }}
                            className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 rounded cursor-move group">
                            <GripVertical className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground flex-shrink-0" />
                            <input type="checkbox" checked={col.visible} onChange={() => toggleColumn(col.id)} onClick={(e) => e.stopPropagation()} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer" />
                            <span className="text-sm flex-1">{col.label}</span>
                            {col.visible ? <Eye className="h-3 w-3 text-muted-foreground" /> : <EyeOff className="h-3 w-3 text-muted-foreground" />}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <ActiveFilterChips
              filters={filters}
              defaultKeys={defaultFilterKeys}
              onRemove={(key) => { handleFilterChange(key, ''); setCurrentPage(1); }}
              onClearAll={handleClearFilters}
            />
            {/* Counts stay visible while refreshing; the pulse marks them
                as provisional until the batched count request lands. */}
            <TabsList className={`mt-3 justify-start ${countsRefreshing ? 'animate-pulse' : ''}`}>
              <TabsTrigger value="all">All Lines</TabsTrigger>
              <TabsTrigger value="needs_action" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                Exceptions{needsActionCount !== null && needsActionCount > 0 ? ` (${needsActionCount.toLocaleString()})` : ''}
              </TabsTrigger>
              <TabsTrigger value="due_previous_weeks" className="data-[state=active]:bg-rose-500 data-[state=active]:text-white">
                Due From Previous Weeks{duePreviousWeeksCount !== null && duePreviousWeeksCount > 0 ? ` (${duePreviousWeeksCount.toLocaleString()})` : ''}
              </TabsTrigger>
              <TabsTrigger value="due_this_week" className="data-[state=active]:bg-sky-500 data-[state=active]:text-white">
                Due This Week{dueThisWeekCount !== null && dueThisWeekCount > 0 ? ` (${dueThisWeekCount.toLocaleString()})` : ''}
              </TabsTrigger>
              <TabsTrigger value="due_next_week" className="data-[state=active]:bg-sky-500 data-[state=active]:text-white">
                Due Next Week{dueNextWeekCount !== null && dueNextWeekCount > 0 ? ` (${dueNextWeekCount.toLocaleString()})` : ''}
              </TabsTrigger>
              <TabsTrigger value="open">Open{openCount !== null && openCount > 0 ? ` (${openCount.toLocaleString()})` : ''}</TabsTrigger>
              <TabsTrigger value="on_hold">On Hold{onHoldCount !== null && onHoldCount > 0 ? ` (${onHoldCount.toLocaleString()})` : ''}</TabsTrigger>
              {!isSupplier && (
                <TabsTrigger value="forecasted" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                  Forecasted{forecastedCount !== null && forecastedCount > 0 ? ` (${forecastedCount.toLocaleString()})` : ''}
                </TabsTrigger>
              )}
            </TabsList>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12"><div className="text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /><p className="mt-2 text-sm text-muted-foreground">Loading schedules...</p></div></div>
            ) : schedules.length === 0 ? (
              <div className="text-center py-12">
                <CalendarRange className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{hasActiveFilters ? 'No schedules match your filters' : 'No schedule lines found'}</p>
                {hasActiveFilters && <button onClick={handleClearFilters} className="mt-4 text-sm text-primary hover:underline">Clear filters</button>}
              </div>
            ) : (
              <>
                <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} itemsPerPage={pageSize} servedPerPage={servedPageRef.current?.served} onPageChange={setCurrentPage} onPageSizeChange={handlePageSizeChange} pageSizeOptions={PAGE_SIZE_CHOICES} itemLabel="schedules" />

                {/* Bulk Action Bar */}
                <div className="mb-4 p-3 bg-muted/40 border border-border rounded-lg flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {bulk.selectedIds.size > 0 ? `${bulk.selectedIds.size} line${bulk.selectedIds.size > 1 ? 's' : ''} selected` : 'No lines selected'}
                    </span>
                    {bulk.selectedIds.size > 0 && (<button onClick={() => bulk.setSelectedIds(new Set())} className="text-xs text-muted-foreground underline hover:text-foreground">Clear</button>)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => bulk.setShowBulkHoldModal(true)} disabled={bulk.selectedIds.size === 0 || bulk.bulkHolding} className={bulk.selectedIds.size > 0 ? 'border-orange-500 text-orange-700 hover:bg-orange-50' : 'opacity-60 cursor-not-allowed'}>
                      {bulk.bulkHolding ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Holding...</> : <><Pause className="h-4 w-4 mr-2" />Put On Hold All</>}
                    </Button>
                    <Button size="sm" onClick={() => bulk.setShowBulkAcceptModal(true)} disabled={bulk.selectedIds.size === 0 || bulk.bulkAccepting} className={bulk.selectedIds.size > 0 ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-200 text-green-600 cursor-not-allowed opacity-60'}>
                      {bulk.bulkAccepting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Accepting...</> : <><Check className="h-4 w-4 mr-2" />Accept All</>}
                    </Button>
                    <Button size="sm" onClick={() => bulk.setShowBulkRejectModal(true)} disabled={bulk.selectedIds.size === 0 || bulk.bulkRejecting} className={bulk.selectedIds.size > 0 ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-200 text-red-400 cursor-not-allowed opacity-60'}>
                      {bulk.bulkRejecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Rejecting...</> : <><X className="h-4 w-4 mr-2" />Reject All</>}
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-350px)] mt-4">
                  <table style={{ tableLayout: 'fixed', width: `${totalTableWidth}px`, minWidth: '100%' }}>
                    <colgroup>
                      <col style={{ width: `${CHECKBOX_COL_WIDTH}px` }} />
                      {visibleCols.map((col) => (
                        <col key={col.id} style={{ width: `${colWidthPx(col)}px` }} />
                      ))}
                    </colgroup>
                    <thead className="sticky top-0 z-20">
                      <tr className="border-b bg-muted/80 backdrop-blur-sm">
                        <th className="text-center py-3 px-2 font-medium">
                          <input type="checkbox" checked={bulk.getSelectableSchedules().length > 0 && bulk.selectedIds.size === bulk.getSelectableSchedules().length} onChange={bulk.handleSelectAll} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" title="Select all eligible schedules" />
                        </th>
                        {visibleCols.map((col) => {
                          const right = ['schedule_qty', 'shipped_qty', 'balance_qty', 'erp_balance_qty', 'unit_price'].includes(col.id);
                          const center = ['on_hold', 'waiting_on', 'line_number', 'schedule_line_number', 'action', 'unit_of_measure'].includes(col.id);
                          const align = right ? 'text-right' : center ? 'text-center' : 'text-left';
                          const justify = center ? 'justify-center' : right ? 'justify-end' : '';
                          return (
                            <th key={col.id} className={`relative ${align} py-3 px-4 font-medium ${col.sortable ? 'cursor-pointer hover:bg-muted/50' : ''}`} onClick={col.sortable ? () => handleSort(col.id) : undefined}>
                              {col.sortable
                                ? <div className={`flex items-center gap-1 ${justify}`}><span className="whitespace-normal break-words">{col.label}</span> {getSortIcon(col.id, sortField, sortDirection)}</div>
                                : <span className="block whitespace-normal break-words">{col.label}</span>}
                              {/* Drag handle on the right edge to resize this column. */}
                              <span
                                role="separator"
                                aria-orientation="vertical"
                                title="Drag to resize column"
                                onPointerDown={(e) => startResize(col.id, e)}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none hover:bg-primary/40 active:bg-primary/60"
                              />
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {schedules.map((s) => (
                        <ScheduleLineRow
                          key={s.id}
                          schedule={s}
                          visibleColumns={visibleCols}
                          formatDate={formatDate}
                          formatNumber={formatNumber}
                          onOpenModal={openModal}
                          onRowClick={(id) => setSelectedScheduleId(id)}
                          isSelected={bulk.selectedIds.has(s.id)}
                          canSelectRow={bulk.canSelect(s)}
                          onToggleSelect={bulk.handleToggleSelect}
                          isSupplier={isSupplier}
                          viewerSide={viewerSideOf(user)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} itemsPerPage={pageSize} servedPerPage={servedPageRef.current?.served} onPageChange={setCurrentPage} onPageSizeChange={handlePageSizeChange} pageSizeOptions={PAGE_SIZE_CHOICES} itemLabel="schedules" />
              </>
            )}
          </CardContent>
        </Tabs>
      </Card>

      <AcceptScheduleModal
        isOpen={modal?.kind === 'accept'}
        schedule={modal?.kind === 'accept' ? modal.schedule : null}
        onClose={() => setModal(null)}
        submitting={modalLoading}
        onSubmit={handleAcceptSubmit}
      />
      <DeclineScheduleModal
        isOpen={modal?.kind === 'decline'}
        schedule={modal?.kind === 'decline' ? modal.schedule : null}
        onClose={() => setModal(null)}
        submitting={modalLoading}
        onSubmit={handleDeclineSubmit}
      />
      <ProposeScheduleDateModal
        isOpen={modal?.kind === 'propose_date'}
        schedule={modal?.kind === 'propose_date' ? modal.schedule : null}
        onClose={() => setModal(null)}
        submitting={modalLoading}
        onSubmit={handleProposeDateSubmit}
      />
      <ProposeScheduleQtyModal
        isOpen={modal?.kind === 'propose_qty'}
        schedule={modal?.kind === 'propose_qty' ? modal.schedule : null}
        onClose={() => setModal(null)}
        submitting={modalLoading}
        onSubmit={handleProposeQtySubmit}
      />
      <HoldScheduleModal
        isOpen={modal?.kind === 'hold'}
        schedule={modal?.kind === 'hold' ? modal.schedule : null}
        onClose={() => setModal(null)}
        submitting={modalLoading}
        onSubmit={handleHoldSubmit}
      />
      <ReleaseScheduleModal
        isOpen={modal?.kind === 'release'}
        schedule={modal?.kind === 'release' ? modal.schedule : null}
        onClose={() => setModal(null)}
        submitting={modalLoading}
        onSubmit={handleReleaseSubmit}
      />
      <CloseHeldScheduleDialog
        open={modal?.kind === 'close_held'}
        schedule={modal?.kind === 'close_held' ? modal.schedule : null}
        onClose={() => setModal(null)}
        onDone={refresh}
      />

      <BulkAcceptModal open={bulk.showBulkAcceptModal} count={bulk.selectedIds.size} loading={bulk.bulkAccepting} onClose={() => bulk.setShowBulkAcceptModal(false)} onConfirm={bulk.handleBulkAccept} />
      <BulkRejectModal open={bulk.showBulkRejectModal} count={bulk.selectedIds.size} loading={bulk.bulkRejecting} onClose={() => bulk.setShowBulkRejectModal(false)} onConfirm={bulk.handleBulkReject} />
      <BulkHoldModal open={bulk.showBulkHoldModal} count={bulk.selectedIds.size} loading={bulk.bulkHolding} onClose={() => bulk.setShowBulkHoldModal(false)} onConfirm={bulk.handleBulkPutOnHold} />

      {selectedScheduleId !== null && (
        <ScheduleDetailModal
          isOpen={true}
          onClose={() => setSelectedScheduleId(null)}
          scheduleId={selectedScheduleId}
          onUpdate={refresh}
        />
      )}
    </div>
  );
}
